import type { IconProps } from '../icon'
import type { SystemProps } from '../system'
import type { LabelProps } from './label.types'

import { omitThemingProps, styled, useStyleConfig, vui } from '../core'
import Icon from '../icon'
import T from '../t'
import Tooltip from '../tooltip'
import { cs, isString } from '../utils'

export const LabelBase = styled.labelBox`
  display: block;
  color: sandstone.10;
  width: 100%;

  &[aria-disabled='true'] {
    cursor: not-allowed;
  }
`

// only `xl` bumps up; sm/md/lg keep the original 'xs' icon size
const fontSizeToIconSize: Record<string, IconProps['size']> = {
  xs: 'xs',
  sm: 'xs',
  md: 'xs',
  lg: 'xs',
  xl: 'sm',
}

type LabelContentProps = Pick<LabelProps, 'text' | 'markAsOptional' | 'optionalText' | 'tooltipText' | 'fontSize'> & {
  helperStyles: SystemProps
}

const LabelContent = ({
  text,
  markAsOptional,
  optionalText = '(optional)',
  tooltipText,
  fontSize,
  helperStyles,
}: LabelContentProps) => {
  const textProps = { variant: 'label' as const, fontSize: 'inherit' as const }
  const helperProps = { ...textProps, ...helperStyles }
  // only token strings are mapped; numeric or responsive fontSize values fall back to 'xs'
  const iconSize = (isString(fontSize) && fontSizeToIconSize[fontSize]) || 'xs'

  return (
    <>
      <T {...textProps}>{text}</T>
      {!!text && markAsOptional && <T {...helperProps}>{optionalText}</T>}
      {!!text && !!tooltipText && (
        <T {...helperStyles}>
          <Tooltip text={tooltipText}>
            <Icon color="seaBlue.28" mb="-3px" name="uiInfoCircle" size={iconSize} role="button" />
          </Tooltip>
        </T>
      )}
    </>
  )
}

/** Displays a label element, optionally with helper text, an optional indicator, and an info tooltip. */
export const Label = vui<'label', LabelProps>((props, ref) => {
  const { children, className, markAsOptional, optionalText, tooltipText, text, ...rest } = omitThemingProps(props)
  const styles = useStyleConfig('Label', props)

  return (
    <T as={LabelBase} className={cs('vui-label', className)} ref={ref} {...rest}>
      {children ?? (
        <LabelContent
          fontSize={rest.fontSize}
          helperStyles={styles.helper}
          markAsOptional={markAsOptional}
          optionalText={optionalText}
          text={text}
          tooltipText={tooltipText}
        />
      )}
    </T>
  )
})

Label.displayName = 'Label'
export default Label
